import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, KeyRound, Brain, Cpu, Zap, Cloud, Key, Plus, Save, X, Star, Loader2, Trash2, Pencil, List, Search } from 'lucide-react';
import { haptic } from '../../telegram';
import { settingsApi } from '../../api/settingsApi';
import type { Provider, ProviderType, ProviderUpsert } from '../../types/contracts';

const TYPE_ICONS: Record<string, any> = { openai: Brain, anthropic: Cpu, google: Zap, mistral: Cloud, 'openai-compatible': Key };
const TYPE_LABELS: Record<string, string> = { openai: 'OpenAI', anthropic: 'Anthropic', google: 'Google AI', mistral: 'Mistral', 'openai-compatible': 'OpenAI-совместимый' };
type Tier = 'brain' | 'mid' | 'cheap';

export function Byok() {
  const nav = useNavigate();
  const [providers, setProviders] = useState<Provider[]>([]);
  const [envProvider, setEnvProvider] = useState<any>(null);
  const [defaultId, setDefaultId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [saveLoading, setSaveLoading] = useState(false);
  const [profile, setProfile] = useState<any>(null);
  const [usage, setUsage] = useState<any>(null);
  const [models, setModels] = useState<string[]>([]);
  const [loadingModels, setLoadingModels] = useState(false);
  const [picker, setPicker] = useState<Tier | null>(null);
  const [modelSearch, setModelSearch] = useState('');
  

  const [fLabel, setFLabel] = useState('');
  const [fType, setFType] = useState<ProviderType>('openai');
  const [fBaseUrl, setFBaseUrl] = useState('');
  // Не подставляем конкретные названия моделей по умолчанию: у разных провайдеров
  // они разные и меняются — пусть пользователь выберёт из списка доступных.
  const [fBrain, setFBrain] = useState('');
  const [fMid, setFMid] = useState('');
  const [fCheap, setFCheap] = useState('');
  const [fKey, setFKey] = useState('');

  const load = () => {
    settingsApi.listProviders()
      .then((d: any) => {
        const list = Array.isArray(d) ? d : (d?.providers ?? []);
        setProviders(list); setEnvProvider(d?.env_provider ?? null); setDefaultId(d?.default_provider ?? null); setLoading(false);
      })
      .catch(() => setLoading(false));
    settingsApi.getProfile().then((p: any) => setProfile(p)).catch(() => {});
    settingsApi.getUsage().then((u: any) => setUsage(u)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const startCreate = () => { haptic('light'); setCreating(true); setEditingId(null); setModels([]); setFLabel(''); setFType('openai'); setFBaseUrl(''); setFBrain(''); setFMid(''); setFCheap(''); setFKey(''); };
  const startEdit = (p: Provider) => { haptic('light'); setCreating(true); setEditingId(p.id); setModels([]); setFLabel(p.label); setFType(p.type); setFBaseUrl((p as any).baseUrl || (p as any).base_url || ''); setFBrain(p.models?.brain || ''); setFMid(p.models?.mid || ''); setFCheap(p.models?.cheap || ''); setFKey(''); };
  const cancelForm = () => { setCreating(false); setEditingId(null); };

  const loadModels = async () => { haptic('light'); setLoadingModels(true); try { const r = await settingsApi.fetchModels({ baseUrl: fBaseUrl.trim() || undefined, key: fKey || undefined, providerId: editingId || undefined, type: fType }); setModels(r.models || []); haptic('medium'); } catch {} finally { setLoadingModels(false); } };
  const openPicker = (tier: Tier) => { haptic('light'); setPicker(tier); setModelSearch(''); if (models.length === 0 && !loadingModels) loadModels(); };
  const pickModel = (val: string) => { haptic('medium'); if (picker === 'brain') setFBrain(val); else if (picker === 'mid') setFMid(val); else if (picker === 'cheap') setFCheap(val); setPicker(null); };

  const save = async () => {
    if (!fLabel.trim()) return; haptic('medium'); setSaveLoading(true);
    try {
      const id = editingId || fLabel.trim().toLowerCase().replace(/[^a-z0-9]/g, '-');
      const data: ProviderUpsert = { id, label: fLabel.trim(), type: fType, baseUrl: fBaseUrl.trim() || undefined, models: { brain: fBrain, mid: fMid, cheap: fCheap }, key: fKey || undefined };
      await settingsApi.upsertProvider(data); cancelForm(); haptic('heavy'); load();
    } catch {} finally { setSaveLoading(false); }
  };
  const setDefault = async (id: string) => { haptic('medium'); try { await settingsApi.setDefaultProvider(id); setDefaultId(id); } catch {} };
  const del = async (id: string) => { haptic('heavy'); try { await settingsApi.deleteProvider(id); setProviders((prev) => prev.filter((p) => p.id !== id)); } catch {} };
  const toggleProfile = async (key: string, val: boolean) => { haptic('light'); try { await settingsApi.saveProfile({ [key]: val } as any); setProfile((prev: any) => ({ ...prev, [key]: val })); } catch {} };
  

  const curVal = picker === 'brain' ? fBrain : picker === 'mid' ? fMid : fCheap;
  const filteredModels = models.filter((m) => { const q = modelSearch.trim().toLowerCase(); return !q || m.toLowerCase().includes(q); });

  return (
    <div className="rf-detail">
      <div className="rf-topbar">
        <button className="rf-back" onClick={() => { haptic('light'); nav(-1 as any); }}><ArrowLeft />Назад</button>
        <div className="rf-topbar-title">API-ключи</div>
        {!creating && <button className="rf-back" onClick={startCreate}><Plus />Добавить</button>}
      </div>

      <div className="rf-detail-scroll">
        {creating ? (
          <>
            <div className="rf-info"><KeyRound /><div><b>{editingId ? 'Изменить провайдера' : 'Новый провайдер'}</b><br />Платформа использует ваши ключи. Они хранятся зашифрованными.</div></div>
            <div className="rf-field"><label>Название</label><input className="rf-input" value={fLabel} onChange={(e) => setFLabel(e.target.value)} placeholder="Например: мой OpenAI" /></div>
            <div className="rf-field"><label>Провайдер</label><select className="rf-select" value={fType} onChange={(e) => setFType(e.target.value as ProviderType)}>{Object.entries(TYPE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</select></div>
            {fType === 'openai-compatible' && (
              <div className="rf-field"><label>Base URL</label><input className="rf-input" value={fBaseUrl} onChange={(e) => setFBaseUrl(e.target.value)} placeholder="https://api.example.com/v1" /><div className="rf-hint">URL для API-запросов (если отличается от стандартного)</div></div>
            )}
            <div className="rf-field"><label>API-ключ</label><input className="rf-input" value={fKey} onChange={(e) => setFKey(e.target.value)} placeholder={editingId ? '••••• оставьте пустым, чтобы не менять' : 'sk-...'} type="password" /><div className="rf-hint">{editingId ? 'Оставьте поле пустым, чтобы сохранить текущий ключ' : 'Ключ из личного кабинета провайдера'}</div></div>
            <div className="rf-seclabel">Модели по уровням</div>
            <div className="rf-hint rf-mb12">Введите название вручную или нажмите на список, чтобы выбрать из доступных.</div>
            <div className="rf-field"><label>Brain (сложные задачи)</label><div className="rf-field-row"><input className="rf-input" value={fBrain} onChange={(e) => setFBrain(e.target.value)} placeholder="gpt-4o" /><button className="rf-pickbtn" onClick={() => openPicker('brain')}><List /></button></div></div>
            <div className="rf-field"><label>Mid (повседневные)</label><div className="rf-field-row"><input className="rf-input" value={fMid} onChange={(e) => setFMid(e.target.value)} placeholder="gpt-4o-mini" /><button className="rf-pickbtn" onClick={() => openPicker('mid')}><List /></button></div></div>
            <div className="rf-field"><label>Cheap (простые операции)</label><div className="rf-field-row"><input className="rf-input" value={fCheap} onChange={(e) => setFCheap(e.target.value)} placeholder="gpt-4o-mini" /><button className="rf-pickbtn" onClick={() => openPicker('cheap')}><List /></button></div></div>
            <div className="rf-btn-row">
              <button className="rf-btn primary" onClick={save} disabled={!fLabel.trim() || saveLoading}>{saveLoading ? <Loader2 className="spin" /> : <Save />}Сохранить</button>
              <button className="rf-btn" onClick={cancelForm}><X />Отмена</button>
            </div>
          </>
        ) : loading ? (
          <div className="rf-loading"><div className="rf-spin" /><p>Загрузка…</p></div>
        ) : (
          <>
            <div className="rf-info"><KeyRound /><div><b>Свой LLM (BYOK)</b><br />Платформа использует ваши ключи. Они хранятся зашифрованными и передаются только провайдеру.</div></div>

            <div className="rf-seclabel">Профиль</div>
            <div className="rf-rows">
              <div className="rf-card rf-conn">
                <div className="rf-conn-row">
                  <div className="rf-conn-info"><div className="rf-conn-name">Подробные статусы</div><div className="rf-conn-sub">Показывать промежуточные шаги выполнения</div></div>
                  <div className={'rf-switch' + (profile?.verbose_status ? ' on' : '')} onClick={() => toggleProfile('verbose_status', !profile?.verbose_status)} role="switch" aria-checked={!!profile?.verbose_status} />
                </div>
              </div>
              {profile && (
                <div className="rf-card rf-conn">
                  <div className="rf-conn-row">
                    <div className="rf-conn-info"><div className="rf-conn-name">Подробные статусы отложенных сценариев</div><div className="rf-conn-sub">Показывать подробные уведомления, когда сценарий запускается по расписанию</div></div>
                    <div className={'rf-switch' + (profile.verbose_for_scheduled ? ' on' : '')} onClick={() => toggleProfile('verbose_for_scheduled', !profile.verbose_for_scheduled)} role="switch" aria-checked={!!profile.verbose_for_scheduled} />
                  </div>
                </div>
              )}
            </div>
            

            {providers.length === 0 && !envProvider ? (
              <div className="rf-empty"><div className="rf-empty-ic"><KeyRound /></div><h3>Нет провайдеров</h3><p>Добавьте свой API-ключ кнопкой «Добавить»</p></div>
            ) : (
              <>
                <div className="rf-seclabel">Провайдеры</div>
                <div className="rf-rows rf-stagger">
                  {envProvider && (
                    <div className="rf-card rf-acard">
                      <div className="rf-acard-head">
                        <div className="rf-row-ic"><Cloud /></div>
                        <div className="rf-acard-info">
                          <div className="rf-acard-name">Провайдер платформы</div>
                          <div className="rf-acard-desc">LLM Платформы</div>
                          <div className="rf-tags">{(defaultId === null || defaultId === envProvider.id) && <span className="rf-tag ok">По умолчанию</span>}</div>
                          <div className="rf-acard-models">
                            <span><b>Brain</b> {envProvider.models.brain}</span>
                            <span><b>Mid</b> {envProvider.models.mid}</span>
                            <span><b>Cheap</b> {envProvider.models.cheap}</span>
                          </div>
                          <div className="rf-hint">Модели платформы подбираются автоматически и могут меняться под нагрузкой.</div>
                          {usage && (<div className="rf-acard-usage">
                            <div className="rf-usage-bar">
                              <div className="rf-usage-fill" style={{width: usage.token_limit ? `${Math.min((usage.total_tokens_used / usage.token_limit) * 100, 100)}%` : '0'}} />
                            </div>
                            <span className="rf-usage-text">{usage.token_limit ? `Использовано ${usage.total_tokens_used.toLocaleString('ru-RU')} из ${usage.token_limit.toLocaleString('ru-RU')} токенов (${Math.min(100, Math.round((usage.total_tokens_used / usage.token_limit) * 100))}%)` : `Использовано ${usage.total_tokens_used.toLocaleString('ru-RU')} токенов · без лимита`}</span>
                          </div>)}
                      </div>
                    </div>
                    <div className="rf-acts">
                        {defaultId !== null && <button className="rf-btn sm icon" title="Сделать основным" onClick={() => setDefault('__env__')}><Star /></button>}
                    </div>
                  </div>
                  )}
                  {envProvider && (
                    <button className="rf-btn primary rf-add-own" onClick={startCreate}><Plus />Добавить своего провайдера</button>
                  )}
                  {providers.map((p) => {
                    const Icon = TYPE_ICONS[p.type] || KeyRound;
                    const isDef = p.is_default || defaultId === p.id;
                    return (
                      <div key={p.id} className="rf-card rf-acard">
                        <div className="rf-acard-head">
                          <div className="rf-row-ic"><Icon /></div>
                          <div className="rf-acard-info">
                            <div className="rf-acard-name">{p.label}</div>
                            <div className="rf-acard-desc">{TYPE_LABELS[p.type] || p.type} · {p.models.brain}</div>
                            <div className="rf-tags">
                              <span className={'rf-tag' + (p.has_key ? ' ok' : '')}>{p.has_key ? 'Ключ установлен' : 'Ключ не задан'}</span>
                              {isDef && <span className="rf-tag accent">По умолчанию</span>}
                            </div>
                          </div>
                        </div>
                        <div className="rf-acts">
                          <button className="rf-btn sm icon" title="Изменить" onClick={() => startEdit(p)}><Pencil /></button>
                          {!isDef && <button className="rf-btn sm icon" title="Сделать основным" onClick={() => setDefault(p.id)}><Star /></button>}
                          <button className="rf-btn sm danger icon" title="Удалить" onClick={() => del(p.id)}><Trash2 /></button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </>
        )}
      </div>

      {picker && (
        <div className="rf-sheet-backdrop" onClick={() => setPicker(null)}>
          <div className="rf-sheet" onClick={(e) => e.stopPropagation()}>
            <div className="rf-sheet-handle" />
            <div className="rf-sheet-head"><div className="rf-sheet-title">Выбор модели{models.length > 0 ? ' · ' + models.length : ''}</div><button className="rf-btn icon ghost" onClick={() => setPicker(null)}><X /></button></div>
            <input className="rf-input rf-sheet-search" placeholder="Поиск или введите свою…" value={modelSearch} onChange={(e) => setModelSearch(e.target.value)} />
            <div className="rf-sheet-list">
              {loadingModels ? (
                <div className="rf-sheet-empty"><Loader2 className="spin" />Загрузка списка…</div>
              ) : filteredModels.length === 0 ? (
                modelSearch.trim() ? (
                  <button className="rf-sheet-item" onClick={() => pickModel(modelSearch.trim())}><Plus />Использовать «{modelSearch.trim()}»</button>
                ) : (
                  <div className="rf-sheet-empty"><Search />{models.length === 0 ? 'Список не загружен — введите модель вручную' : 'Ничего не найдено'}</div>
                )
              ) : (
                filteredModels.map((m) => (
                  <button key={m} className={'rf-sheet-item' + (curVal === m ? ' sel' : '')} onClick={() => pickModel(m)}>{m}</button>
                ))
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
